import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import useAxios from "../../../utils/useAxios";
import useFetchStages from "../../../hooks/useFetchStages";
import StageTabsScroll from "../components/StageTabsScroll";
import EmptyData from "../../EmptyData";
import PageLoader from "../../../components/PageLoader";
import { openTournamentSettings } from "../../../utils/tournamentSettingsBus";
import TournamentGroups from "../groups/TournamentGroups";
import TournamentGrids from "./TournamentGrids";
import useViewMode from "../../../hooks/useViewMode";

// Merges group-stage and draw-stage into one stage list/selector, same
// client-side reconstruction TournamentDrawStagesSettings.js already uses
// (order/draw_number are independent backend sequences - see that file's
// own comment) - then renders whichever of TournamentGroups/TournamentGrids
// matches the selected stage's type, in "controlled" mode (stageControl
// prop) so neither component runs its own stage fetch/selector.
const TournamentDrawStages = (props) => {
    const { id } = useParams();
    const location = useLocation();
    const history = useHistory();
    const api = useAxios();

    const groupsFetch = useFetchStages(api, `/api/v1.0/tournaments/${id}/group-stage/`);
    const drawsFetch = useFetchStages(api, `/api/v1.0/tournaments/${id}/draw-stage/`);

    // Survives the TournamentGroups<->TournamentGrids swap below (a real
    // unmount/remount, different components entirely) when the selected
    // stage crosses from a group stage to a bracket stage or back - see
    // useBottomSheet's own persistTargetRef comment. Lives here rather
    // than inside either component since it has to outlive whichever one
    // of them is currently mounted.
    const sheetTargetRef = useRef("peek");

    // Owned here (not inside TournamentGrids itself) purely so it survives
    // the TournamentGroups<->TournamentGrids swap and both format-specific
    // hooks can share this one "which stage is active" gate - the toggle's
    // own UI lives down in TournamentGrids' place-tabs row (onViewModeChange
    // prop below), not on this component's own stage-tabs bar.
    const { viewMode: gridsViewMode, toggleViewMode: toggleGridsViewMode } = useViewMode("grids_view_mode", "classic");
    // Same idea for group stages - Матриця (the pairwise cross-table, the
    // long-standing default) vs Залік (a padel Americano/Mexicano-style
    // standings row: M/W/L instead of the per-opponent grid); its own UI
    // lives in TournamentGroups' group-tabs-row.
    const { viewMode: groupsViewMode, toggleViewMode: toggleGroupsViewMode } = useViewMode("groups_view_mode", "matrix");

    useEffect(() => {
        groupsFetch.fetchStages();
        drawsFetch.fetchStages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isStagesLoading = groupsFetch.isLoading || drawsFetch.isLoading;

    const merged = useMemo(() => {
        const groupStages = groupsFetch.stages.map(s => ({ ...s, __format: "groups", __pos: s.order || 0 }));
        const drawStages = drawsFetch.stages.map(s => ({ ...s, __format: "draws", __pos: s.draw_number || 0 }));
        return [...groupStages, ...drawStages].sort((a, b) => a.__pos - b.__pos);
    }, [groupsFetch.stages, drawsFetch.stages]);

    const [activeId, setActiveId] = useState(null);

    // ?stage= carries the stage's real id, not its position in `merged` -
    // matches every other tab (TournamentGrids standalone/TournamentGroups/
    // TournamentSchedule already key off the real id). A position/index is
    // only ever meaningful relative to ONE particular `merged` array, and
    // this page is never the only thing computing one: the preview iframe
    // (TournamentPreviewFrame.js) keeps a second, fully independent copy of
    // this exact component mounted at the same time (the real page hidden
    // behind it, per App.js's own comment on why it stays mounted), each
    // with its own groupsFetch/drawsFetch pair resolving on its own
    // schedule, mirroring their own ?stage= back and forth via postMessage/
    // history.replace. An index computed against one copy's `merged` (its
    // own groups-stages array + draws-stages array, fetched independently)
    // isn't guaranteed to land on the same stage once reinterpreted against
    // the other copy's - two lists built from two unrelated async fetches
    // racing on their own timing are never guaranteed to settle in the same
    // order/composition at the same moment. An id has no such dependency -
    // it resolves correctly the instant `merged` contains it, regardless of
    // which of the two copies is asking or what order their fetches landed
    // in.
    useEffect(() => {
        if (isStagesLoading) return;
        if (merged.length === 0) {
            setActiveId(null);
            return;
        }
        setActiveId(prev => {
            if (prev && merged.some(s => s.id === prev)) return prev;
            const urlStage = new URLSearchParams(location.search).get("stage");
            const matched = merged.find(s => String(s.id) === String(urlStage));
            return (matched || merged[0]).id;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merged, isStagesLoading]);

    const handleStageChange = useCallback((stageId) => {
        setActiveId(stageId);
        const newParams = new URLSearchParams(location.search);
        newParams.set("stage", stageId);
        newParams.delete("gridTab");
        history.replace({ search: newParams.toString() });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const activeEntry = merged.find(s => s.id === activeId);
    const activeFormat = activeEntry?.__format;

    if (isStagesLoading) {
        return <PageLoader />;
    }

    return (
        <div id="draw-stages-block">
            {merged.length === 0 ? (
                <EmptyData
                    title="No stages created"
                    text="Groups or brackets have not been created yet. Data will appear here once a stage is generated."
                    actionLabel="Go to settings"
                    onAction={props.is_authorized ? () => openTournamentSettings("draw-stages") : undefined}
                />
            ) : (
                <>
                    {/* Classic/Pro and Matrix/Standings toggles no longer live here -
                        they moved down to sit next to the place/group-tabs row
                        each child renders for itself (TournamentGrids' grid-tab-item
                        row / TournamentGroups' group-tabs-row), same level for both
                        formats. This row is now just the stage picker. */}
                    <ul className="tabs" style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <StageTabsScroll
                            stages={merged}
                            activeStage={activeId}
                            onStageChange={handleStageChange}
                        />
                    </ul>

                    {activeFormat === "groups" ? (
                        <TournamentGroups
                            {...props}
                            sheetTargetRef={sheetTargetRef}
                            viewMode={groupsViewMode}
                            onViewModeChange={toggleGroupsViewMode}
                            stageControl={{
                                stages: groupsFetch.stages,
                                activeStage: activeId,
                                setActiveStage: handleStageChange,
                                isLoading: isStagesLoading,
                            }}
                        />
                    ) : (
                        <TournamentGrids
                            {...props}
                            sheetTargetRef={sheetTargetRef}
                            viewMode={gridsViewMode}
                            onViewModeChange={toggleGridsViewMode}
                            stageControl={{
                                stages: drawsFetch.stages,
                                activeStage: activeId,
                                setActiveStage: handleStageChange,
                                isLoading: isStagesLoading,
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default TournamentDrawStages;
